import React, { FC, Fragment, useEffect, useState } from "react";
import {
  Button,
  Descriptions,
  DescriptionsProps,
  Flex,
  Modal,
  ModalProps,
  Spin,
  Table,
  TableProps,
  Tag,
  Tooltip,
  Typography,
  App,
  Space
} from "antd";
import { ConfirmedEnum } from "@/types/inventory";
import { CountOrderItem, CountOrderStateEnum } from "@/types/inventoryV2";
import dayjs from "dayjs";
import { useTranslation } from 'react-i18next';
import { BarcodesShow } from "@/components/BarcodesShow";
import useCountingFetch from "../hooks/useCountingFetch";
import { getUserAvatarTip, getUserName } from "@/utils/user";
import WMSAvatar from "@/styles/WMSAvatar";
import useWarehouseParams from "@/pages/Warehouse/hooks/useWarehouseParams";
import { StoreTypeEnum } from "@/types/product";
import { dateFormat } from "@/utils/format";
import SText from "@/styles/SText";
import CountAddV2 from "../../components/CountAddV2";
import { countDel } from "@/apis/inventoryv2";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { numFormmater } from "@/pages/Requirement/util";

const { Title } = Typography;

interface OrderDetailProps extends ModalProps {
  id: string | null;
  handleOk: (id: string) => void;
  handleCancel: (type: "UODATA" | "DEL" | "CLOSE") => void;
}

const OrderDetail: FC<OrderDetailProps> = ({ id, handleCancel, handleOk, ...modalProps }) => {
  const { t } = useTranslation();
  const { type } = useWarehouseParams();
  const { modal, notification } = App.useApp();


  const [deLoading, detail, { fetchDetails, setDetail }] = useCountingFetch();

  const [openBarcodes, setOpenBarcodes] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [barcodeLoading] = useState<boolean>(false);
  const [barcodesCount, setBarcodesCount] = useState<number>(0);
  const [barcodes, setBarcodes] = useState<string[] | null>(null);

  useEffect(() => {
    if (id) {
      fetchDetails(id);
    }
  }, [id]);

  const descriptionItems: DescriptionsProps["items"] = [
    {
      label: t('counting.operator'),
      children: detail?.creator && (
        <>
          <WMSAvatar
            uniChar={detail?.creator.id ?? "1"}
            avatarId={detail?.creator.avatar ?? ""}
            creator={detail?.creator}
            alt={getUserAvatarTip(detail?.creator)}
            size={24}
          />
          <span> {getUserName(detail.creator)}</span>
        </>
      ),
      span: 1,
    },
    {
      label: t('counting.createdAt'),
      children: detail && dateFormat(dayjs(detail?.createdAt), true),
      span: 1,
    },
    {
      label: t('counting.invStatus'),
      children: detail && (
        <Tag color={detail.orderState === CountOrderStateEnum.DONE ? "green" : "blue"}>
          {detail.orderState === CountOrderStateEnum.DONE ? t('counting.done') : t('counting.counting')}
        </Tag>
      ),
    }
  ];


  const numColumns: TableProps<CountOrderItem>["columns"] = [
    {
      title: t('order.serialMumber'),
      key: 'index',
      render: (_, __, index) => index + 1,
    },
    {
      title: t('order.productCode'),
      dataIndex: "product",
      key: "product",
      render: (product) => <Tag color="green">{product.code}</Tag>
    },
    {
      title: t('product.name'),
      dataIndex: "product",
      key: "product",
      render: (product) => (
        <Tooltip title={product.name}>
          <span>{product.name}</span>
        </Tooltip>
      ),
    },
    {
      title: "lot",
      dataIndex: "lotStr",
      key: "lotStr",
      render: (lotStr) => (
        <Tooltip title={lotStr}>
          <span>{lotStr}</span>
        </Tooltip>
      ),
    },
    {
      width: 150,
      title: t('order.quantity'),
      key: 'quantity',
      render: (_, item) => numFormmater(item.counting || 0)
    }
  ];



  const onBarcodeCancel = () => {
    setOpenBarcodes(false);
    setBarcodes([]);
    setBarcodesCount(0);
  }

  const onCancelClick = () => {
    setBarcodes(null);
    handleCancel("CLOSE");
  }

  const handelDel = () => {
    if (!detail) return;

    modal.confirm({
      title: t('counting.confirmDelete'),
      icon: <ExclamationCircleOutlined />,
      content: t('counting.confirmDeleteContent'),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await countDel(detail.id);
          notification.success({
            placement: 'top',
            message: t('counting.deleteSuccess')
          });
          handleCancel("DEL");
        } catch (error: any) {
          notification.error({
            placement: 'top',
            message: t('counting.deleteFailed'),
            description: error?.message || error
          });
        }
      }
    });
  }

  return (
    <>
      <Modal
        width={1200}
        title={
          <Flex justify="space-between" style={{ width: "100%", boxSizing: "border-box", paddingRight: 38 }} >
            <SText text={t('counting.countingDetail')} />
            <Space>
              {detail?.orderState === CountOrderStateEnum.COUNTING && <Button type="primary" onClick={() => setOpen(true)} >{t('counting.continueCounting')}</Button>}
              {detail?.orderState === CountOrderStateEnum.COUNTING && <Button type="link" danger onClick={handelDel} >{t('common.del')}</Button>}
            </Space>
          </Flex>
        }
        {...modalProps}
        onCancel={() => onCancelClick()}
        footer={null}
      >
        {deLoading && <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><Spin /></div>}
        {detail && !deLoading &&
          <Fragment>
            <Descriptions bordered items={descriptionItems} />
            <Title level={5}>{t('counting.countingProductData')}</Title>
            <Table
              columns={numColumns}
              dataSource={detail?.items ?? []}
              pagination={false}
            />
          </Fragment>
        }
      </Modal>
      {type === StoreTypeEnum.CODE && <BarcodesShow loading={barcodeLoading} open={openBarcodes} barcodes={barcodes || []} barcodesCount={barcodesCount} onCancel={onBarcodeCancel} />}
      {open && detail && <CountAddV2 open={open} onClose={(delOrder) => { setOpen(false); delOrder && handleCancel("DEL") }} countId={detail.id} onSuccess={(order, type) => {
        if (type === "UPDATE") setDetail(order)
      }} />}
    </>
  );
};

export default OrderDetail;
